<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')"/>
    <MenuHealthCompact v-else/>

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Financial Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Online Payments</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Online Payments</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="goFinancialPayments" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Financial Payments</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="col-12 md:col-12">
                        <div class="card p-fluid">
                            <div class="p-grid">

                                <DataTable
                                    v-model:lazyParams="lazyParams"
                                    :value="payments"
                                    lazy
                                    paginator
                                    size="small"
                                    :ptOptions="{ mergeSections: true, classNameMergeStrategy: 'replace' }"
                                    :pt="{headerCell: { class: 'py-1 px-2' }, bodyCell: { class: 'p-0' }, bodyRow: { class: 'p-0' }}"
                                    :rows="10"
                                    :totalRecords="totalRecords"
                                    :loading="loading"
                                    :rowsPerPageOptions="[10, 20, 50, 100]"
                                    :sortMode="'single'"
                                    :rowHover="true"
                                    :striped-rows="true"
                                    :reorderableColumns="true"
                                    filterDisplay="menu"
                                    responsiveLayout="scroll"
                                    :globalFilterFields="['id', 'uniqueId', 'clientName', 'created', 'amount']"
                                    @page="onPage"
                                    @sort="onSort"
                                    @filter="onFilter">

                                    <template #header>
                                        <div class="p-grid">
                                            <div class="grid">
                                                <div class="col-12 lg:col-6 p-2">
                                                    <MultiSelect :modelValue="selectedColumns"
                                                                 :options="columnsFullList"
                                                                 optionLabel="header"
                                                                 @update:modelValue="onToggleColumns"
                                                                 display="chip"
                                                                 placeholder="Select Columns"/>
                                                </div>
                                                <div class="col-12 lg:col-6 p-2">
                                                    <Dropdown v-model="selectedStandardFilter"
                                                              :options="statusTypesLimited"
                                                              optionLabel="name" optionValue="code"
                                                              placeholder="Select a Filter"
                                                              @update:modelValue="onToggleStandardFilters"/>
                                                </div>

                                            </div>

                                            <div class="grid">
                                                <div class="col-12 lg:col-6 p-2">
                                                    <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                            class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                                </div>
                                                <div class="col-12 lg:col-6 p-2">
                                                    <Button type="button" icon="pi pi-replay"
                                                            label="Restore to Original View"
                                                            class="p-button-outlined" @click="restoreToOriginal()"/>
                                                </div>
                                            </div>

                                            <div class="p-col-12 p-md-2">
                                                <span class="p-input-icon-left">
                                                    <i class="pi pi-search"/>
                                                    <InputText v-model="filters['global'].value"
                                                               placeholder="Keyword Search on ..."
                                                               style="width: 100%"
                                                               @keyup.enter="onSearch"/>

                                                </span>
                                            </div>
                                        </div>
                                    </template>

                                    <template #empty>
                                        No payments found.
                                    </template>

                                    <template #loading>
                                        Loading payments. Please wait.
                                    </template>

                                    <Column
                                        field="selectAnOption"
                                        header=""
                                        :headerStyle="{ width: '2rem' }"
                                        :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                                        <template #body="{ data, index }">
                                            <Button icon="pi pi-ellipsis-v"
                                                    class="p-button-sm p-button-text p-button-rounded m-0"
                                                    @click="$refs['menu' + index].toggle($event)"/>
                                            <Menu
                                                :ref="'menu' + index"
                                                :model="getFilteredItems(data)"
                                                :popup="true"/>
                                        </template>
                                    </Column>

                                    <Column :headerStyle="{ width: '2rem' }"
                                            :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                            field="created" header="Created Date" :sortable="true"/>

                                    <template v-for="col in selectedColumns" :key="col.field">

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-if="col.field === 'amount'" :field="col.field"
                                                :header="col.header" :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Amount</span>
                                                {{ formatAmount(data.amount) }}
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else-if="col.field === 'status'" :field="col.field" :header="col.header"
                                                :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Status</span>
                                                <span v-if="data.status === 'I'"
                                                      :class="'status-badge status-' + data.status">Initiated</span>
                                                <span v-if="data.status === 'P'"
                                                      :class="'status-badge status-' + data.status">Paid</span>
                                                <span v-if="data.status === 'E'"
                                                      :class="'status-badge status-' + data.status">Error</span>
                                                <span v-if="data.status === 'R'"
                                                      :class="'status-badge status-' + data.status">Refunded</span>
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else-if="col.field === 'sourceIntegration'" :field="col.field" :header="col.header"
                                                :sortable="col.sortable">
                                            <template #body="{ data }">
                                                <span class="p-column-title">Source</span>
                                                <span v-if="data.sourceIntegration === 'DNA_PAYMENTS'"
                                                      :class="'source-badge source-' + data.sourceIntegration">DNA Payments</span>
                                                <span v-if="data.sourceIntegration === 'PAY_FAST'"
                                                      :class="'source-badge source-' + data.sourceIntegration">Pay Fast</span>
                                                <span v-if="data.sourceIntegration === 'STRIPE'"
                                                      :class="'source-badge source-' + data.sourceIntegration">Stripe</span>
                                            </template>
                                        </Column>

                                        <Column :headerStyle="{ width: '2rem' }"
                                                :bodyStyle="{ width: '10rem', padding: '0.25rem' }"
                                                v-else :field="col.field" :header="col.header"
                                                :sortable="col.sortable"/>

                                    </template>

                                </DataTable>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Refund Online Payment {{ this.payment.id }}</h3>

                    <div class="field">
                        <label for="refundConfirmationResponse">Do You Wish to Refund this Payment?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select a Response"/>
                    </div>

                    <Button label="Refund" :loading="submitted" class="mr-2 mb-2" @click="refundPayment"></Button>
                    <Button label="Cancel" :loading="submitted" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Details for Online Payment {{ this.payment.paymentReference }}</h3>

                    <div class="field">
                        <label>Created</label>
                        {{ this.payment.created }}
                    </div>

                    <div class="field">
                        <label>Status</label>
                        <span v-if="this.payment.status === 'I'"
                              :class="'status-badge status-' + this.payment.status">Initiated</span>
                        <span v-if="this.payment.status === 'P'"
                              :class="'status-badge status-' + this.payment.status">Paid</span>
                        <span v-if="this.payment.status === 'E'"
                              :class="'status-badge status-' + this.payment.status">Error</span>
                        <span v-if="this.payment.status === 'R'"
                              :class="'status-badge status-' + this.payment.status">Refunded</span>
                    </div>

                    <div class="field">
                        <label>Amount</label>
                        {{ formatAmount(this.payment.amount) }}
                    </div>

                    <div class="field">
                        <label>Response Message</label>
                        {{ this.payment.responseMessage }}
                    </div>

                    <div class="field">
                        <label>Response Code</label>
                        {{ this.payment.responseCode }}
                    </div>

                    <div class="field">
                        <label>Response Date</label>
                        {{ this.payment.responseDate }}
                    </div>

                    <div class="field">
                        <label>Response Reference</label>
                        {{ this.payment.responsePaymentReference }}
                    </div>

                    <div style="height: 30px;"></div>

                    <Button label="Cancel" :loading="submitted" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import PaymentService from "@/service/PaymentService";
import numeral from "numeral";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'id', header: 'Payment #', sortable: true},
            {field: 'uniqueId', header: 'Invoice #', sortable: false},
            {field: 'amount', header: 'Amount', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'paymentReference', header: 'Reference', sortable: true},
            {field: 'sourceIntegration', header: 'Source', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
        ];

        this.columnsFullList = [
            {field: 'id', header: 'Payment #', sortable: true},
            {field: 'uniqueId', header: 'Invoice #', sortable: false},
            {field: 'amount', header: 'Amount', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'paymentReference', header: 'Reference', sortable: true},
            {field: 'sourceIntegration', header: 'Source', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
        ];

        this.selectedColumns = this.columnsDefault;
    },
    async mounted() {
        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[OnlinePayments][mounted] System is active.");
            console.log("[OnlinePayments][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[OnlinePayments][mounted] System is NOT active.");
            console.log("[OnlinePayments][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[OnlinePayments][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();
    },
    data() {

        return {
            totalRecords: 0,
            loading: false,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {}
            },
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            submitted: false,
            filteredClients: [],
            filteredItemsCache: new Map(),
            payment: {},
            payments: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypesLimited: [
                {name: 'All', code: 'A'}
            ],
        }
    },
    watch: {
        payments() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[OnlinePayments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    computed: {},
    methods: {
        async loadCompanyContext() {
            try {
                try {
                    // Attempt to get user data
                    const userData = await UserService.getUser(this.userStore.user.id);
                    this.profile = userData.data;
                } catch (e) {
                    console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                    this.userStore.clearUser();
                    this.$router.push('login');
                    return;
                }

                this.profile.companyId = this.userStore.activeCompanyId;

                if (!this.userStore.userConfiguration) {
                    const userConfigurationData = await UserService.getUserConfig(this.userStore.user.id);
                    this.userStore.setUserConfiguration(userConfigurationData.data);
                }

                this.userConfiguration = this.userStore.userConfiguration;


                if (!this.userStore.companyConfiguration) {
                    const companyConfigurationData = await CompanyService.getCompanyConfiguration(this.profile.companyId);
                    this.userStore.setCompanyConfiguration(companyConfigurationData.data);
                }

                this.companyConfiguration = this.userStore.companyConfiguration;

                await this.loadData();

                this.loading = false;

            } catch (e) {
                console.error("[OnlinePayments][mounted] Error fetching additional data = ", e);
            }

            console.log("[OnlinePayments][mounted] this.payments = ", this.payments);
            console.log("[OnlinePayments][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[OnlinePayments][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        async loadData() {
            this.loading = true;
            try {
                console.log("[OnlinePayments][loadData] Fetching clients with params:", this.lazyParams, "Search:", this.filters['global']?.value);

                const response = await PaymentService.getCompanyOnlinePaymentsLazyLoading(
                    this.profile.companyId,
                    this.lazyParams,
                    this.filters['global']?.value || '',
                    this.selectedStandardFilter, null, null);

                console.log("[OnlinePayments][loadData] API Response:", response);
                console.log("[OnlinePayments][loadData] response.data.content:", response.data?.content);
                console.log("[OnlinePayments][loadData] response.data.totalElements:", response.data?.totalElements);

                this.payments = Array.isArray(response.data.content) ? response.data.content : [];

                this.totalRecords = response.data?.totalElements || 0;

                console.log("[OnlinePayments][loadData] Clients Updated:", this.clients, "Total Records:", this.totalRecords);

            } catch (error) {
                console.error("[OnlinePayments][loadData] Error loading payments:", error);
                this.payments = [];
                this.totalRecords = 0;
            }
            this.loading = false;
        },
        onPage(event) {
            console.log("[OnlinePayments][onPage] Page Event:", event);
            this.lazyParams.first = event.first;
            this.lazyParams.rows = event.rows;
            this.loadData();
        },
        onSort(event) {
            this.lazyParams.sortField = event.sortField;
            this.lazyParams.sortOrder = event.sortOrder;
            this.loadData();
        },
        onFilter(event) {
            this.lazyParams.filters = event.filters;
            this.loadData();
        },
        onSearch() {
            console.log("[OnlinePayments][onSearch] Searching with:", this.filters['global'].value);
            this.lazyParams.first = 0;
            this.loadData();
        },
        onToggleStandardFilters() {
            console.log("[OnlinePayments][onToggleStandardFilters] Applying Standard Filter:", this.selectedStandardFilter);
            this.lazyParams.first = 0;
            this.loadData();
        },
        clearFilter() {
            this.initFilters();
            this.lazyParams.filters = {};
            this.lazyParams.first = 0;
            this.loadData();
        },
        restoreToOriginal() {
            this.initFilters();
            this.lazyParams.filters = {};
            this.lazyParams.first = 0;
            this.loadData();
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'uniqueId': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'clientName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'created': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'amount': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        callReset() {
            this.activeTab = 1;
        },
        goFinancialPayments() {
            this.$router.push("commonFinancialPayments");
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'View Details',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewPayment(data);
                }
            });

            if ((data.status === 'P') && (data.sourceIntegration === 'DNA_PAYMENTS')) {
                items.push({
                    label: 'Refund',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callRefundPayment(data);
                    }
                });
            }

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            } else {
                return numeral(0.00).format('0,0.00');
            }
        },
        callViewPayment(payment) {
            this.payment = {...payment};
            this.activeTab = 3;
            console.log("[OnlinePayments][callViewPayment] this.payment = ", this.payment);
        },
        callRefundPayment(payment) {
            this.payment = {...payment};
            this.confirmationResponse = 'N';
            this.activeTab = 2;
            console.log("[OnlinePayments][callRefundPayment] this.payment = ", this.payment);
        },
        async refundPayment() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.payment.id) {

                    try {
                        const response = await PaymentService.refundOnlineGBPPayment(
                            this.payment.companyId,
                            this.payment.id
                        );
                        console.log('[OnlinePayments][refundPayment] Response = ', response);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Refunded payment',
                            life: 3000
                        });

                        await this.loadData();

                    } catch (error) {
                        console.error('[OnlinePayments][refundPayment] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to refund payment. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-P {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-E {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-I {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-R {
        background: #B3E5FC;
        color: #23547B;
    }
}

.source-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.source-badge {
    &.source-DNA_PAYMENTS {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.source-PAY_FAST {
        background: #ECCFFF;
        color: #694382;
    }

    &.source-STRIPE {
        background: #B3E5FC;
        color: #23547B;
    }
}

.tabview-custom {

    i,
    span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {

    i,
    span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}
</style>
